// Smart Daara - Enrollment Management Page for Educators
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { enrollmentAPI, userAPI, courseAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { 
  UserPlus, Check, X, Clock, Users, BookOpen, Loader2, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

export default function EnrollmentManagement() {
  const { user } = useAuth();
  const { language } = useLanguage();
  
  const [pendingEnrollments, setPendingEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  
  // Direct enrollment modal
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const pendingRes = await enrollmentAPI.getPendingEnrollments();
      setPendingEnrollments(pendingRes.data || []);
    } catch (error) {
      console.error('Error loading enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEnrollmentData = async () => {
    try {
      const [studentsRes, coursesRes] = await Promise.all([
        userAPI.listStudents({ tenant_id: user?.tenant_id }),
        courseAPI.listCourses({ tenant_id: user?.tenant_id })
      ]);
      setStudents(studentsRes.data?.items || studentsRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Error loading data for enrollment:', error);
    }
  };

  const handleApprove = async (enrollmentId) => {
    setProcessing(enrollmentId);
    try {
      await enrollmentAPI.processEnrollment(enrollmentId, 'approve');
      toast.success(language === 'fr' ? 'Inscription approuvée!' : 'Enrollment approved!');
      setPendingEnrollments(prev => prev.filter(e => e.enrollment_id !== enrollmentId));
    } catch (error) {
      console.error('Error approving:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (enrollmentId) => {
    setProcessing(enrollmentId);
    try {
      await enrollmentAPI.processEnrollment(enrollmentId, 'reject');
      toast.success(language === 'fr' ? 'Inscription refusée' : 'Enrollment rejected');
      setPendingEnrollments(prev => prev.filter(e => e.enrollment_id !== enrollmentId));
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setProcessing(null);
    }
  };

  const openEnrollModal = () => {
    loadEnrollmentData();
    setEnrollModalOpen(true);
  };

  const handleDirectEnroll = async () => {
    if (!selectedStudent || !selectedCourse) {
      toast.error(language === 'fr' ? 'Sélectionnez un étudiant et un cours' : 'Select a student and course');
      return;
    }

    setEnrolling(true);
    try {
      await enrollmentAPI.enrollStudent(selectedCourse, selectedStudent);
      toast.success(language === 'fr' ? 'Étudiant inscrit avec succès!' : 'Student enrolled successfully!');
      setEnrollModalOpen(false);
      setSelectedStudent('');
      setSelectedCourse('');
    } catch (error) {
      console.error('Error enrolling:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'inscription' : 'Enrollment error');
    } finally {
      setEnrolling(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: language === 'fr' ? fr : enUS
      });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enrollment-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {language === 'fr' ? 'Gestion des Inscriptions' : 'Enrollment Management'}
          </h1>
          <p className="text-slate-500">
            {language === 'fr' 
              ? 'Validez les demandes et inscrivez des étudiants' 
              : 'Validate requests and enroll students'}
          </p>
        </div>
        <Button 
          className="bg-emerald-700 hover:bg-emerald-800"
          onClick={openEnrollModal}
        >
          <UserPlus className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Inscrire un étudiant' : 'Enroll Student'}
        </Button>
      </div>

      {/* Pending Enrollments */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            {language === 'fr' ? 'Demandes en attente' : 'Pending Requests'}
            {pendingEnrollments.length > 0 && (
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                {pendingEnrollments.length}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {language === 'fr' 
              ? 'Demandes d\'inscription en attente de validation' 
              : 'Enrollment requests waiting for approval'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingEnrollments.length === 0 ? (
            <div className="text-center py-8">
              <UserCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">
                {language === 'fr' ? 'Aucune demande en attente' : 'No pending requests'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingEnrollments.map((enrollment) => (
                <div 
                  key={enrollment.enrollment_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  data-testid={`pending-enrollment-${enrollment.enrollment_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {enrollment.student.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {enrollment.student.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-medium text-slate-900">
                        {enrollment.course.title_fr || enrollment.course.title}
                      </p>
                      <p className="text-sm text-slate-500">
                        {formatTime(enrollment.requested_at)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleReject(enrollment.enrollment_id)}
                        disabled={processing === enrollment.enrollment_id}
                      >
                        {processing === enrollment.enrollment_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-700 hover:bg-emerald-800"
                        onClick={() => handleApprove(enrollment.enrollment_id)}
                        disabled={processing === enrollment.enrollment_id}
                      >
                        {processing === enrollment.enrollment_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            {language === 'fr' ? 'Approuver' : 'Approve'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Direct Enrollment Modal */}
      <Dialog open={enrollModalOpen} onOpenChange={setEnrollModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Inscrire un étudiant' : 'Enroll a Student'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {language === 'fr' ? 'Étudiant' : 'Student'}
              </label>
              <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                <SelectTrigger data-testid="select-student">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner un étudiant' : 'Select a student'} />
                </SelectTrigger>
                <SelectContent>
                  {students.map(student => (
                    <SelectItem key={student.id} value={student.user_id || student.id}>
                      {student.first_name} {student.last_name} - {student.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {language === 'fr' ? 'Cours' : 'Course'}
              </label>
              <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                <SelectTrigger data-testid="select-course">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner un cours' : 'Select a course'} />
                </SelectTrigger>
                <SelectContent>
                  {courses.map(course => (
                    <SelectItem key={course.id} value={course.id}>
                      {course.title_fr || course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              className="bg-emerald-700 hover:bg-emerald-800"
              onClick={handleDirectEnroll}
              disabled={enrolling || !selectedStudent || !selectedCourse}
            >
              {enrolling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              {language === 'fr' ? 'Inscrire' : 'Enroll'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
